import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Description,
  Drawer,
  FieldError,
  ListBox,
  Spinner,
  type OverlayTriggerState,
} from '@heroui/react';
import { AsyncButton } from '../../../components/ui/async-button';
import {
  getCondominiumDetails,
  listCondominiumsWithMemberCount,
  type CondominiumDetails,
} from '../../../api/condominiums';
import type { CreateMembershipPayload } from '../../../api/memberships';
import type { UserWithMemberships } from '../../../api/users';
import { DrawerSelectField } from '../../../components/ui/drawer-form-field';
import { drawerBodyClassName, drawerFooterClassName, sideDrawerDialogClassName } from '../../../components/ui/side-drawer-classes';
import { StatusCallout } from '../../../components/ui/status-callout';
import { isProfileComplete } from '../../../utils/profileValidation';
import { getUserDisplayName } from '../utils/user-labels';

interface ApartmentOption {
  id: string;
  label: string;
}

interface AssociateMembershipDrawerProps {
  state: OverlayTriggerState;
  user: UserWithMemberships | null;
  onSubmit: (payload: CreateMembershipPayload) => Promise<void>;
}

export function AssociateMembershipDrawer({ state, user, onSubmit }: AssociateMembershipDrawerProps) {
  const [condominiumId, setCondominiumId] = useState('');
  const [apartmentId, setApartmentId] = useState('');
  const [role, setRole] = useState<CreateMembershipPayload['role']>('USER');
  const [status, setStatus] = useState<NonNullable<CreateMembershipPayload['status']>>('ACTIVE');
  const [condominiums, setCondominiums] = useState<
    Awaited<ReturnType<typeof listCondominiumsWithMemberCount>>
  >([]);
  const [condominiumDetails, setCondominiumDetails] = useState<CondominiumDetails | null>(null);
  const [loadingCondominiums, setLoadingCondominiums] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!state.isOpen) return;
    setCondominiumId('');
    setApartmentId('');
    setRole('USER');
    setStatus('ACTIVE');
    setCondominiumDetails(null);
    setError(null);
    setSubmitting(false);

    setLoadingCondominiums(true);
    void listCondominiumsWithMemberCount()
      .then(setCondominiums)
      .catch(() => setCondominiums([]))
      .finally(() => setLoadingCondominiums(false));
  }, [state.isOpen]);

  useEffect(() => {
    if (!condominiumId) {
      setCondominiumDetails(null);
      setApartmentId('');
      return;
    }

    setLoadingDetails(true);
    setError(null);
    void getCondominiumDetails(condominiumId)
      .then((details) => {
        setCondominiumDetails(details);
        setApartmentId('');
      })
      .catch(() => {
        setCondominiumDetails(null);
        setError('Não foi possível carregar os apartamentos deste condomínio.');
      })
      .finally(() => setLoadingDetails(false));
  }, [condominiumId]);

  const profileComplete = user
    ? isProfileComplete({
        firstName: user.firstName,
        lastName: user.lastName,
        documentNumber: user.documentNumber,
        phone: user.phone,
      })
    : false;

  const apartmentOptions = useMemo<ApartmentOption[]>(() => {
    if (!condominiumDetails) return [];

    return condominiumDetails.blocks.flatMap((block) =>
      block.apartments
        .filter((apartment) => apartment.isActive)
        .map((apartment) => ({
          id: apartment.id,
          label: `${block.name} · Apt ${apartment.number}${apartment.floor ? ` (${apartment.floor})` : ''}`,
        })),
    );
  }, [condominiumDetails]);

  const isValid = !!user && profileComplete && !!condominiumId && !!apartmentId;

  const handleSubmit = async () => {
    if (!user || !isValid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        userId: user.id,
        apartmentId,
        role,
        status,
      });
      state.close();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Não foi possível criar a associação.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const userLabel = user ? getUserDisplayName(user) : '';

  return (
    <Drawer state={state}>
      <Drawer.Backdrop isDismissable>
        <Drawer.Content placement="right">
          <Drawer.Dialog className={sideDrawerDialogClassName}>
            <Drawer.Header>
              <Drawer.Heading>Associar ao condomínio</Drawer.Heading>
              <Drawer.CloseTrigger />
            </Drawer.Header>
            <Drawer.Body className={drawerBodyClassName}>
              {user ? (
                <Description>
                  Vincular <strong>{userLabel}</strong> a um condomínio. Escolha também a unidade
                  inicial — depois você pode ajustar o apartamento na página do condomínio.
                </Description>
              ) : null}

              {!profileComplete ? (
                <StatusCallout status="warning" title="Perfil incompleto">
                  O usuário precisa completar nome, CPF e telefone antes de ser associado.
                </StatusCallout>
              ) : null}

              <DrawerSelectField
                label="Condomínio"
                value={condominiumId || null}
                onChange={setCondominiumId}
                isDisabled={submitting || loadingCondominiums || !profileComplete}
              >
                <ListBox>
                  {condominiums.map((condominium) => (
                    <ListBox.Item
                      key={condominium.id}
                      id={condominium.id}
                      textValue={condominium.name}
                    >
                      {condominium.name}
                      <ListBox.ItemIndicator />
                    </ListBox.Item>
                  ))}
                </ListBox>
              </DrawerSelectField>

              <DrawerSelectField
                label="Apartamento inicial"
                value={apartmentId || null}
                onChange={setApartmentId}
                isDisabled={
                  submitting || loadingDetails || !condominiumId || apartmentOptions.length === 0
                }
              >
                <ListBox>
                  {apartmentOptions.map((apartment) => (
                    <ListBox.Item key={apartment.id} id={apartment.id} textValue={apartment.label}>
                      {apartment.label}
                      <ListBox.ItemIndicator />
                    </ListBox.Item>
                  ))}
                </ListBox>
              </DrawerSelectField>

              {condominiumId && !loadingDetails && apartmentOptions.length === 0 ? (
                <Description>Nenhum apartamento ativo neste condomínio.</Description>
              ) : null}

              <DrawerSelectField
                label="Papel"
                value={role}
                onChange={(value) => setRole(value as CreateMembershipPayload['role'])}
                isDisabled={submitting || !profileComplete}
              >
                <ListBox>
                  <ListBox.Item id="MANAGER" textValue="Gestor">
                    Gestor
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                  <ListBox.Item id="USER" textValue="Usuário">
                    Usuário
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                </ListBox>
              </DrawerSelectField>

              <DrawerSelectField
                label="Status"
                value={status}
                onChange={(value) =>
                  setStatus(value as NonNullable<CreateMembershipPayload['status']>)
                }
                isDisabled={submitting || !profileComplete}
              >
                <ListBox>
                  <ListBox.Item id="ACTIVE" textValue="Ativo">
                    Ativo
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                  <ListBox.Item id="INVITED" textValue="Convidado">
                    Convidado
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                  <ListBox.Item id="SUSPENDED" textValue="Suspenso">
                    Suspenso
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                </ListBox>
              </DrawerSelectField>

              {error ? <FieldError>{error}</FieldError> : null}
            </Drawer.Body>
            <Drawer.Footer className={drawerFooterClassName}>
              <Button variant="ghost" isDisabled={submitting} onPress={state.close}>
                Cancelar
              </Button>
              <AsyncButton
                variant="primary"
                isPending={submitting}
                isDisabled={!isValid}
                onPress={handleSubmit}
              >
                Associar
              </AsyncButton>
            </Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </Drawer>
  );
}
